import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, TextField, Button, Typography,
  InputAdornment, IconButton, Alert, CircularProgress,
  useTheme, alpha, darken,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLoginMutation } from '../../store/api/authApi';
import { setCredentials } from './authSlice';
import { registerPush } from '../../lib/pushNotifications';
import { useAppDispatch } from '../../hooks/useAppStore';
import { getCrmPalette } from '../../theme/brand';
import { GridSnake } from '../../components/GridSnake';
import { BoostGlyph } from '../../components/BoosterMark';
import { setFavicon, BOOSTER_FAVICON } from '../../lib/favicon';

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Contraseña requerida'),
});
type LoginForm = z.infer<typeof loginSchema>;

const FEATURES = [
  'Gestión de clientes y propiedades',
  'Finanzas y comisiones automatizadas',
  'Competencia y métricas de equipo',
];

// Sitio público de Booster — el logo y el estado del sistema enlazan acá.
const BOOSTER_URL = 'https://boostercrm.app';

// Grano sutil para romper el plano (evita el look plano/genérico).
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

// ─── Login Page ───────────────────────────────────────────────────────────────
export function LoginPage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useAppDispatch();
  // Destino original si AuthGuard te mandó acá desde un deep-link (link de Gustavo,
  // ficha de propiedad, etc.). Volvemos ahí tras loguear en vez de al dashboard.
  const fromPath  = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
  const theme     = useTheme();
  const isDark    = theme.palette.mode === 'dark';
  const P         = getCrmPalette();
  /**
   * EL ACENTO DEL LOGIN — sale de la paleta, siempre.
   *
   * Este login es COMPARTIDO: lo usan Imperium (oro), Booster (celeste desde el 25/08/2026)
   * y cada tenant white-label con su color. `P.brand.golden` ya es el correcto en los tres
   * casos, porque `applyCrmAccent` muta la paleta con el color del tenant antes de que esto
   * se lea.
   *
   * ⛔ ACÁ HABÍA UNA COPIA A MANO del acento de Booster (`#22D3EE`/`#67E8F9`) para el caso
   * "Booster sin tenant". Era el MISMO valor que la paleta, duplicado — y por eso al pasar
   * la marca al celeste este login se quedaba cian sin que nada fallara. El color de la
   * marca vive en `BOOSTER_PALETTE` y en ningún otro lado.
   */
  const ACENTO         = P.brand.golden;
  const ACENTO_SUAVE   = P.brand.goldenLight;
  const DISPLAY   = "'Orbitron', 'Inter', sans-serif"; // fuente display de marca (Orbitron)

  const [login, { isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError]   = useState('');

  // Pestaña del navegador específica del login (pisa el "Booster CRM" del provider).
  useEffect(() => {
    document.title = 'BOOSTER | Hecho para los agentes inmobiliarios';
    setFavicon(BOOSTER_FAVICON);
  }, []);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setServerError('');
    try {
      const result = await login(data).unwrap();
      dispatch(setCredentials({ user: result.user, accessToken: result.accessToken }));
      // Registrar push notifications en segundo plano (no bloquea el login)
      registerPush(result.accessToken).catch(() => {});
      // Landing page por rol — staff de plataforma entra a la consola, support a su panel.
      const landingByRole: Record<string, string> = {
        booster_admin: '/platform',
        booster_support: '/support',
      };
      // Si venías de un deep-link (AuthGuard guardó `from`), volvemos ahí; si no,
      // landing por rol. Nunca volvemos a /login para no quedar en loop.
      const target = fromPath?.pathname && fromPath.pathname !== '/login'
        ? `${fromPath.pathname}${fromPath.search ?? ''}`
        : (landingByRole[result.user.role] ?? '/dashboard');
      navigate(target, { replace: true });
    } catch (e: any) {
      const status = e?.status;
      const msg = e?.data?.error?.message;
      // 403 típicamente es roles de portal — mostramos el mensaje exacto del backend
      if (status === 403 && msg) {
        setServerError(msg);
      } else {
        setServerError(msg ?? 'Email o contraseña incorrectos');
      }
    }
  };

  // Entrada escalonada (staggered) — un solo page-load orquestado.
  const enter = (delay: number) => ({
    opacity: 0,
    animation: 'bst-rise 0.7s cubic-bezier(0.22,1,0.36,1) forwards',
    animationDelay: `${delay}ms`,
  });

  // Inputs: glass sutil + focus-ring emerald.
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: alpha('#FFFFFF', 0.02),
      borderRadius: '12px',
      transition: 'background-color .2s ease, box-shadow .2s ease',
      '& fieldset': { borderColor: alpha('#FFFFFF', 0.10), transition: 'border-color .2s ease' },
      '&:hover fieldset': { borderColor: alpha(ACENTO, 0.45) },
      '&.Mui-focused': { bgcolor: alpha(ACENTO, 0.05), boxShadow: `0 0 0 3px ${alpha(ACENTO, 0.16)}` },
      '&.Mui-focused fieldset': { borderColor: ACENTO },
    },
  };

  const inputLabelSx = {
    display: 'block', mb: 0.85,
    fontSize: 11, fontWeight: 700, letterSpacing: '0.14em',
    color: 'text.secondary', textTransform: 'uppercase' as const,
  };

  // Lockup de marca: monograma "B" + wordmark BOOSTER (emerald, Orbitron).
  // Clickeable → sitio público de Booster.
  const Mark = (
    <Box
      component="a"
      href={BOOSTER_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Ir a boostercrm.app"
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 1.25,
        textDecoration: 'none', color: 'inherit', cursor: 'pointer',
        transition: 'opacity .2s ease', '&:hover': { opacity: 0.82 },
      }}
    >
      <Box sx={{
        width: 38, height: 38, borderRadius: '11px', flexShrink: 0,
        display: 'grid', placeItems: 'center',
        // Mismo look que el FAB de Gustavo: verde semi-transparente (glass) + borde.
        background: alpha(ACENTO, 0.22),
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: `1px solid ${alpha(ACENTO, 0.55)}`,
        boxShadow: `0 6px 18px ${alpha(ACENTO, 0.3)}, inset 0 1px 0 ${alpha('#FFFFFF', 0.15)}`,
      }}>
        <BoostGlyph size={23} />
      </Box>
      <Box component="span" style={{ fontFamily: DISPLAY }} sx={{ fontWeight: 800, fontSize: 19, letterSpacing: '0.16em', color: 'text.primary' }}>
        BOOSTER
      </Box>
    </Box>
  );

  return (
    <>
      <style>{`
        @keyframes bst-rise { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
        @keyframes bst-status { 0%,100% { transform: scale(1); opacity: .85; } 50% { transform: scale(2.4); opacity: 0; } }
        @keyframes bst-sheen { 0% { transform: translateX(-130%) skewX(-12deg); } 55%,100% { transform: translateX(280%) skewX(-12deg); } }
      `}</style>

      <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default', position: 'relative', overflow: 'hidden' }}>

        {/* ── Atmósfera de fondo: glows emerald + grano ── */}
        <Box aria-hidden sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: `radial-gradient(1100px 620px at 6% -8%, ${alpha(ACENTO, 0.12)} 0%, transparent 60%),
                       radial-gradient(900px 600px at 102% 112%, ${alpha(ACENTO_SUAVE, 0.08)} 0%, transparent 55%)`,
        }} />
        <Box aria-hidden sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          opacity: 0.045, mixBlendMode: 'overlay',
          backgroundImage: GRAIN, backgroundSize: '180px 180px',
        }} />

        {/* ── Panel izquierdo (solo desktop) ── */}
        <Box sx={{
          display: { xs: 'none', md: 'flex' },
          flex: '0 0 52%',
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: { md: 6, lg: 8 },
          position: 'relative',
          overflow: 'hidden',
          background: isDark
            // El tinte de la marca activa (celeste en Booster, oro en Imperium, el suyo en un
            // tenant white-label) abriendo y cerrando el gradiente. Antes había un ternario con
            // el tinte de Booster escrito a mano; era la misma copia que el acento y falló igual.
            ? `linear-gradient(155deg, ${P.brand.loginTint} 0%, #070707 55%, ${darken(P.brand.loginTint, 0.45)} 100%)`
            : `linear-gradient(160deg, #eef3f0 0%, #f7faf8 55%, #eaf1ed 100%)`,
        }}>
          {/* Grid sutil con máscara radial */}
          <Box aria-hidden sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            backgroundImage: `linear-gradient(${alpha(ACENTO, 0.05)} 1px, transparent 1px),
                              linear-gradient(90deg, ${alpha(ACENTO, 0.05)} 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
            maskImage: 'radial-gradient(120% 100% at 28% 18%, #000 28%, transparent 78%)',
            WebkitMaskImage: 'radial-gradient(120% 100% at 28% 18%, #000 28%, transparent 78%)',
          }} />

          {/* Pulse sobre el grid */}
          <GridSnake isDark={isDark} color={ACENTO} />

          {/* Viñeta para foco */}
          <Box aria-hidden sx={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(80% 80% at 50% 38%, transparent 52%, rgba(0,0,0,0.55) 100%)',
          }} />

          {/* Top: lockup de marca */}
          <Box sx={{ position: 'relative', zIndex: 1, ...enter(40) }}>{Mark}</Box>

          {/* Middle: eyebrow + headline + features */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2.5, ...enter(140) }}>
              <Box sx={{ width: 26, height: 2, borderRadius: 2, background: P.gradients.golden }} />
              <Typography style={{ fontFamily: DISPLAY }} sx={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.32em', color: ACENTO, textTransform: 'uppercase' }}>
                CRM Inmobiliario
              </Typography>
            </Box>

            <Typography
              variant="h3"
              fontWeight={800}
              style={{ fontFamily: DISPLAY }}
              sx={{
                color: isDark ? '#F0EDE6' : '#0E1411',
                fontSize: { md: '1.9rem', lg: '2.2rem' },
                lineHeight: 1.18,
                letterSpacing: '-0.01em',
                mb: 2.5, maxWidth: 460,
                ...enter(220),
              }}
            >
              Hecho para los agentes inmobiliarios
            </Typography>

            <Typography sx={{
              color: isDark ? 'rgba(240,237,230,0.5)' : 'rgba(14,20,17,0.6)',
              maxWidth: 380, lineHeight: 1.75, fontSize: 15,
              ...enter(300),
            }}>
              Clientes, propiedades, finanzas y equipo — todo integrado en una plataforma diseñada para crecer.
            </Typography>

            <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 1.75 }}>
              {FEATURES.map((f, i) => (
                <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ...enter(380 + i * 90) }}>
                  <Box sx={{
                    width: 22, height: 22, borderRadius: '7px', flexShrink: 0,
                    display: 'grid', placeItems: 'center',
                    background: alpha(ACENTO, 0.12),
                    border: `1px solid ${alpha(ACENTO, 0.32)}`,
                  }}>
                    <CheckRoundedIcon sx={{ fontSize: 13, color: ACENTO }} />
                  </Box>
                  <Typography sx={{ color: isDark ? 'rgba(240,237,230,0.62)' : 'rgba(14,20,17,0.7)', fontWeight: 500, fontSize: 14 }}>
                    {f}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Bottom: estado del sistema + copyright */}
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...enter(720) }}>
            <Box
              component="a"
              href={BOOSTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Sistema operativo — ir a boostercrm.app"
              sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                textDecoration: 'none', cursor: 'pointer',
                '&:hover .sys-label': { color: ACENTO },
              }}
            >
              <Box sx={{ position: 'relative', width: 7, height: 7 }}>
                <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: ACENTO }} />
                <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: ACENTO, animation: 'bst-status 2.4s ease-out infinite' }} />
              </Box>
              <Typography className="sys-label" sx={{ fontSize: 11, letterSpacing: '0.14em', color: isDark ? 'rgba(240,237,230,0.4)' : 'rgba(14,20,17,0.45)', transition: 'color .2s ease' }}>
                boostercrm.app
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: isDark ? 'rgba(240,237,230,0.28)' : 'rgba(14,20,17,0.35)' }}>
              © {new Date().getFullYear()} BOOSTER CRM · v3.0
            </Typography>
          </Box>

          {/* Seam luminoso entre paneles */}
          <Box aria-hidden sx={{
            position: 'absolute', top: 0, bottom: 0, right: 0, width: '1px',
            background: `linear-gradient(180deg, transparent, ${alpha(ACENTO, 0.4)} 38%, ${alpha(ACENTO, 0.4)} 62%, transparent)`,
          }} />
        </Box>

        {/* ── Panel derecho: formulario en glass ── */}
        <Box sx={{
          flex: 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: { xs: 3, sm: 5, md: 6 },
          position: 'relative', zIndex: 1,
        }}>
          <Box sx={{ width: '100%', maxWidth: 420, ...enter(160) }}>
            <Box sx={{
              position: 'relative', overflow: 'hidden', borderRadius: '20px',
              p: { xs: 3, sm: 4 },
              bgcolor: isDark ? alpha('#0E1411', 0.55) : alpha('#FFFFFF', 0.75),
              backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
              border: `1px solid ${alpha('#FFFFFF', isDark ? 0.08 : 0.5)}`,
              boxShadow: `0 30px 80px -20px rgba(0,0,0,${isDark ? 0.8 : 0.25}),
                          0 0 0 1px ${alpha(ACENTO, 0.05)},
                          inset 0 1px 0 ${alpha('#FFFFFF', isDark ? 0.06 : 0.6)}`,
              // borde-luz superior emerald
              '&::before': {
                content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px',
                background: `linear-gradient(90deg, transparent, ${alpha(ACENTO, 0.7)}, transparent)`,
              },
              // sheen lento que cruza el panel
              '&::after': {
                content: '""', position: 'absolute', top: 0, left: 0, width: '45%', height: '100%',
                background: `linear-gradient(100deg, transparent, ${alpha('#FFFFFF', 0.06)}, transparent)`,
                animation: 'bst-sheen 7.5s ease-in-out infinite', pointerEvents: 'none',
              },
            }}>
              {/* Lockup en mobile */}
              <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 3 }}>
                {Mark}
              </Box>

              {/* Header */}
              <Box sx={{ mb: 3.5 }}>
                <Typography variant="h5" fontWeight={800} color="text.primary" style={{ fontFamily: DISPLAY }} sx={{ letterSpacing: '0.01em', mb: 0.75 }}>
                  Bienvenido de vuelta
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ingresá tus credenciales para acceder
                </Typography>
              </Box>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>

                  {serverError && (
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{serverError}</Alert>
                  )}

                  <Box>
                    <Typography component="label" sx={inputLabelSx}>Email</Typography>
                    <TextField
                      placeholder="tu@email.com"
                      type="email"
                      fullWidth
                      autoFocus
                      autoComplete="email"
                      {...register('email')}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MailOutlineRoundedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography component="label" sx={inputLabelSx}>Contraseña</Typography>
                    <TextField
                      placeholder="••••••••"
                      type={showPassword ? 'text' : 'password'}
                      fullWidth
                      autoComplete="current-password"
                      {...register('password')}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{ color: 'text.disabled' }}
                            >
                              {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={isLoading}
                    disableElevation
                    style={{ fontFamily: DISPLAY }}
                    endIcon={!isLoading ? <ArrowForwardRoundedIcon /> : undefined}
                    sx={{
                      mt: 0.5,
                      py: 1.5,
                      fontSize: 14.5,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      borderRadius: '12px',
                      boxShadow: `0 8px 24px ${alpha(ACENTO, 0.35)}`,
                      transition: 'transform .18s ease, box-shadow .18s ease, background .18s ease',
                      '& .MuiButton-endIcon': { transition: 'transform .2s ease' },
                      '&:hover': {
                        boxShadow: `0 12px 34px ${alpha(ACENTO, 0.5)}`,
                        transform: 'translateY(-1px)',
                        '& .MuiButton-endIcon': { transform: 'translateX(4px)' },
                      },
                      '&:active': { transform: 'translateY(0)' },
                      '&.Mui-disabled': { opacity: 0.65 },
                    }}
                  >
                    {isLoading
                      ? <CircularProgress size={20} sx={{ color: P.brand.onAccent }} />
                      : 'Iniciar sesión'}
                  </Button>

                  <Box sx={{ textAlign: 'center', mt: 0.25 }}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => navigate('/recuperar-contrasena')}
                      sx={{
                        background: 'none', border: 'none', cursor: 'pointer', p: 0,
                        fontSize: 13, color: 'text.secondary', fontFamily: 'inherit',
                        transition: 'color .2s ease',
                        '&:hover': { color: ACENTO, textDecoration: 'underline' },
                      }}
                    >
                      ¿Olvidaste tu contraseña?
                    </Box>
                  </Box>

                </Box>
              </form>
            </Box>

            {/* Footer mobile */}
            <Typography sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mt: 3, fontSize: 11, color: 'text.disabled' }}>
              © {new Date().getFullYear()} BOOSTER CRM · v3.0
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
